import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SlackIcon, DiscordIcon } from '../../../components/shared/SocialIcons';
import ToggleSwitch from '../../../components/shared/ToggleSwitch';
import {
  Loader2,
  Check,
  Send
} from 'lucide-react';

const MonitoringWebhooks = ({
  webhookConfig,
  savingWebhooks,
  testingWebhook,
  onSaveWebhooks,
  onTestWebhook,
  updateWebhookConfig
}) => {
  if (!webhookConfig) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Webhook Integrations</h3>
            <p className="text-sm text-gray-600">Send alerts to Slack and Discord channels</p>
          </div>
          <Button
            onClick={onSaveWebhooks}
            disabled={savingWebhooks}
            className="bg-pink-600 hover:bg-pink-700"
          >
            {savingWebhooks ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="space-y-8">
          {/* Slack Integration */}
          <div className="p-6 border rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <SlackIcon />
                </div>
                <div>
                  <h4 className="font-semibold">Slack</h4>
                  <p className="text-sm text-gray-600">Receive alerts in your Slack channel</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Enable</span>
                <ToggleSwitch
                  enabled={webhookConfig.slack_enabled}
                  onToggle={() => updateWebhookConfig('slack_enabled', !webhookConfig.slack_enabled)}
                  activeColor="bg-purple-600"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Webhook URL</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="text"
                    placeholder="https://hooks.slack.com/services/..."
                    value={webhookConfig.slack_webhook_url || ''}
                    onChange={(e) => updateWebhookConfig('slack_webhook_url', e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => onTestWebhook('slack')}
                    disabled={!webhookConfig.slack_webhook_url || testingWebhook === 'slack'}
                  >
                    {testingWebhook === 'slack' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Get your webhook URL from Slack: Apps → Incoming Webhooks → Add New Webhook
                </p>
              </div>
            </div>
          </div>

          {/* Discord Integration */}
          <div className="p-6 border rounded-lg bg-gradient-to-r from-indigo-50 to-blue-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <DiscordIcon />
                </div>
                <div>
                  <h4 className="font-semibold">Discord</h4>
                  <p className="text-sm text-gray-600">Receive alerts in your Discord channel</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Enable</span>
                <ToggleSwitch
                  enabled={webhookConfig.discord_enabled}
                  onToggle={() => updateWebhookConfig('discord_enabled', !webhookConfig.discord_enabled)}
                  activeColor="bg-indigo-600"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Webhook URL</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="text"
                    placeholder="https://discord.com/api/webhooks/..."
                    value={webhookConfig.discord_webhook_url || ''}
                    onChange={(e) => updateWebhookConfig('discord_webhook_url', e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => onTestWebhook('discord')}
                    disabled={!webhookConfig.discord_webhook_url || testingWebhook === 'discord'}
                  >
                    {testingWebhook === 'discord' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Get your webhook URL from Discord: Server Settings → Integrations → Webhooks → New Webhook
                </p>
              </div>
            </div>
          </div>

          {/* Notification Preferences */}
          <Card className="p-6 bg-gray-50">
            <h4 className="font-semibold mb-4">Notification Preferences</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Warning Alerts</p>
                  <p className="text-sm text-gray-600">Send notifications for warning-level alerts</p>
                </div>
                <ToggleSwitch
                  enabled={webhookConfig.notify_on_warning}
                  onToggle={() => updateWebhookConfig('notify_on_warning', !webhookConfig.notify_on_warning)}
                  activeColor="bg-yellow-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Critical Alerts</p>
                  <p className="text-sm text-gray-600">Send notifications for critical-level alerts</p>
                </div>
                <ToggleSwitch
                  enabled={webhookConfig.notify_on_critical}
                  onToggle={() => updateWebhookConfig('notify_on_critical', !webhookConfig.notify_on_critical)}
                  activeColor="bg-red-500"
                />
              </div>
            </div>
          </Card>

          {/* Setup Instructions */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">How to Set Up</h4>
            <div className="text-sm text-blue-700 space-y-2">
              <p><strong>Slack:</strong> Go to your Slack workspace → Apps → Search "Incoming Webhooks" → Add to channel → Copy webhook URL</p>
              <p><strong>Discord:</strong> Go to your Discord server → Server Settings → Integrations → Webhooks → New Webhook → Copy URL</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MonitoringWebhooks;
